import { Fragment, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { TileDeFato } from "../componentes/CalendarioUi";
import { Cabine } from "../componentes/FerramentaUi";
import { HonorarioDialogo } from "../componentes/PeritoDialogos";
import { BarraDeComposicao, BarrasPorMes } from "../componentes/PeritoGraficos";
import { Alternador, BOTAO_SECUNDARIO, EtapaProgresso, Rotulo, Secao } from "../componentes/PeritoUi";
import { CAMPO } from "../componentes/ui";
import { baixarCsv, montarCsv, numeroParaCsv } from "../lib/csv";
import { formatarData, hoje } from "../lib/perito/datas";
import {
  agruparCarne, anosDisponiveis, calcularFaixas, listarLancamentos, proximosRecebimentos, resumir, serieMensal,
  todosOsHonorarios, totaisDeHonorarios, valoresLiberados, type Lancamento, type VisaoDoCarne,
} from "../lib/perito/financeiro";
import { mascaraCpfCnpj } from "../lib/perito/cpfCnpj";
import { formatBRL, plural } from "../lib/perito/format";
import { usePerito } from "../lib/perito/store";
import { nomeDaPericia } from "./PeritoPericias";

// O financeiro da Central do Perito (fase B), portado de `src/routes/_authenticated/financeiro.tsx` e das duas
// abas dele (`FluxoJudicialTab` e `CarneLeaoTab`): a visão geral dos honorários, o caminho do dinheiro no
// processo judicial e o Carnê-Leão do ano. A aba, o ano e o agrupamento ficam no ENDEREÇO
// (`?aba=carne-leao&ano=2025&agrupar=trimestral`).
//
// As contas são de `lib/perito/financeiro.ts`, provadas em `tests/perito-painel.mjs`, e o defeito de lá que não
// veio é o do recebido: lá o gráfico somava o recebido no mês do VENCIMENTO, aqui no mês em que ENTROU. As
// planilhas saem por `lib/csv.ts` (o Excel do Brasil abre direto, e a fórmula num nome é neutralizada); o PDF e
// o Excel de lá vêm com os Relatórios, na fase C.

const RAIZ = "/ferramenta/central-do-perito";
const ABAS = [
  { id: "visao", rotulo: "Visão geral" },
  { id: "fluxo", rotulo: "Fluxo judicial" },
  { id: "carne-leao", rotulo: "Carnê-Leão" },
] as const;
type Aba = (typeof ABAS)[number]["id"];
const AGRUPAR = [
  { id: "mensal", rotulo: "Mensal" },
  { id: "trimestral", rotulo: "Trimestral" },
  { id: "anual", rotulo: "Anual" },
] as const;

const pct = (v: number) => `${v.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}%`;

export default function PeritoFinanceiro() {
  const { pericias } = usePerito();
  const [query, setQuery] = useSearchParams();
  const aba: Aba = (ABAS.find((a) => a.id === query.get("aba"))?.id ?? "visao") as Aba;
  const mudar = (mudancas: Record<string, string | null>) => {
    const nova = new URLSearchParams(query);
    for (const [k, v] of Object.entries(mudancas)) (v ? nova.set(k, v) : nova.delete(k));
    setQuery(nova, { replace: true });
  };

  const totais = useMemo(() => totaisDeHonorarios(todosOsHonorarios(pericias)), [pericias]);
  const semHonorarios = totais.lancamentos === 0;

  return (
    <div className="flex flex-col gap-5" data-financeiro data-aba={aba}>
      <Cabine rotulo="Gestão" titulo="Financeiro"
        descricao="Os honorários previstos e recebidos, o caminho do dinheiro no processo judicial e o Carnê-Leão do ano."
        fatos={semHonorarios ? undefined : (
          <>
            <TileDeFato rotulo="Total previsto" valor={<span data-fato="total">{formatBRL(totais.total)}</span>} apoio={plural(totais.lancamentos, "lançamento", "lançamentos")} />
            <TileDeFato rotulo="Recebido" valor={<span data-fato="recebido">{formatBRL(totais.recebido)}</span>} apoio={`${pct(totais.total ? (totais.recebido / totais.total) * 100 : 0)} do previsto`} />
            <TileDeFato rotulo="A receber" valor={<span data-fato="a-receber">{formatBRL(totais.aReceber)}</span>} apoio="ainda pendente" />
            <TileDeFato rotulo="Média por lançamento" valor={<span data-fato="media">{formatBRL(totais.total / totais.lancamentos)}</span>} apoio="o valor previsto" />
          </>
        )} />

      {/* Num contêiner: solto na coluna flexível, o alternador esticava até a largura toda. */}
      <div><Alternador rotulo="Aba do financeiro" opcoes={ABAS} valor={aba} aoMudar={(a) => mudar({ aba: a === "visao" ? null : a, ano: null, agrupar: null })} /></div>

      {aba === "visao" && <VisaoGeral />}
      {aba === "fluxo" && <FluxoJudicial />}
      {aba === "carne-leao" && <CarneLeao query={query} mudar={mudar} />}
    </div>
  );
}

function VisaoGeral() {
  const { pericias } = usePerito();
  const serie = useMemo(() => serieMensal(pericias, 12), [pericias]);
  const comHonorarios = pericias.filter((p) => p.honorarios.length > 0);
  const vazio = serie.every((m) => m.previsto === 0 && m.recebido === 0);

  const baixarSerie = () => baixarCsv(
    montarCsv(["Mês", "Previsto (vence no mês)", "Recebido (entrou no mês)"], serie.map((m) => [m.mes, numeroParaCsv(m.previsto), numeroParaCsv(m.recebido)])),
    `honorarios-por-mes-${hoje()}.csv`,
  );
  const baixarPorPericia = () => baixarCsv(
    montarCsv(["Processo", "Apelido", "Tribunal", "Tipo", "Total", "Recebido", "A receber"],
      comHonorarios.map((p) => {
        const t = totaisDeHonorarios(p.honorarios);
        return [p.numeroProcesso, p.apelido ?? "", p.tribunal, p.tipoPericia, numeroParaCsv(t.total), numeroParaCsv(t.recebido), numeroParaCsv(t.aReceber)];
      })),
    `honorarios-por-pericia-${hoje()}.csv`,
  );

  return (
    <>
      <Secao nome="evolucao" titulo="Os últimos 12 meses" descricao="O previsto pelo mês do vencimento, e o recebido pelo mês em que entrou."
        acao={!vazio ? <button type="button" className={BOTAO_SECUNDARIO} onClick={baixarSerie} data-csv-serie>Baixar a planilha</button> : undefined}>
        {vazio ? <p className="text-sm text-cinza">Ainda não há honorário com vencimento ou recebimento nestes meses. Lance os honorários na ficha de cada perícia.</p> : <BarrasPorMes serie={serie} altura={260} />}
      </Secao>

      <Secao nome="por-pericia" titulo="Honorários por perícia"
        acao={comHonorarios.length > 0 ? <button type="button" className={BOTAO_SECUNDARIO} onClick={baixarPorPericia} data-csv-por-pericia>Baixar a planilha</button> : undefined}>
        {comHonorarios.length === 0 ? (
          <p className="text-sm text-cinza">Os honorários lançados em cada perícia aparecem aqui, com o quanto já entrou.</p>
        ) : (
          <div className="-mx-5 overflow-x-auto sm:-mx-6">
            <table className="w-full min-w-[40rem] text-sm">
              <thead className="border-y border-borda bg-fundo">
                <tr className="text-left text-[11px] font-bold tracking-[0.12em] text-cinza uppercase [&>th]:px-5 [&>th]:py-3 sm:[&>th]:px-6">
                  <th>Perícia</th><th className="text-right">Recebido</th><th className="text-right">Total</th><th className="w-56">Recebido do total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-borda">
                {comHonorarios.map((p) => {
                  const t = totaisDeHonorarios(p.honorarios);
                  const parte = t.total ? Math.min(100, (t.recebido / t.total) * 100) : 0;
                  return (
                    <tr key={p.id} className="[&>td]:px-5 [&>td]:py-3 sm:[&>td]:px-6" data-linha-pericia={p.id}>
                      <td>
                        <Link to={`${RAIZ}/pericias/${p.id}?aba=financeiro`} className="font-semibold text-titulo hover:text-link hover:underline">{nomeDaPericia(p)}</Link>
                        <span className="block text-xs text-cinza tabular-nums">{p.numeroProcesso}</span>
                      </td>
                      <td className="text-right font-semibold text-aviso-tinta tabular-nums" data-recebido>{formatBRL(t.recebido)}</td>
                      <td className="text-right tabular-nums" data-total>{formatBRL(t.total)}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="h-2 flex-1 overflow-hidden rounded-full bg-chip"><div className="h-full rounded-full bg-aviso-tinta" style={{ width: `${parte}%` }} /></div>
                          <span className="w-10 text-right text-xs font-semibold text-cinza tabular-nums">{pct(parte)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Secao>
    </>
  );
}

function FluxoJudicial() {
  const { pericias } = usePerito();
  const { faixas, total } = useMemo(() => calcularFaixas(pericias), [pericias]);
  const janelas = useMemo(() => proximosRecebimentos(pericias), [pericias]);
  const liberados = useMemo(() => valoresLiberados(pericias), [pericias]);

  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5" data-faixas>
        {faixas.map((f) => (
          <div key={f.id} className="surface p-4" data-faixa-cartao={f.id}>
            <div className="flex items-center gap-2"><span aria-hidden="true" className={`size-2.5 shrink-0 rounded-full ${f.cor}`} /><Rotulo>{f.rotulo}</Rotulo></div>
            <p className="mt-2 font-display text-xl font-bold text-titulo tabular-nums" data-valor>{formatBRL(f.valor)}</p>
            <p className="text-xs text-cinza">{plural(f.quantas, "perícia", "perícias")}</p>
          </div>
        ))}
      </div>

      <Secao nome="composicao" titulo="Onde está o seu dinheiro?" descricao={`Total em jogo: ${formatBRL(total)}. O valor de cada perícia está na faixa da etapa em que ela está.`}>
        {total === 0 ? <p className="text-sm text-cinza">Lance os honorários nas perícias para ver a distribuição por etapa.</p> : <BarraDeComposicao faixas={faixas} />}
      </Secao>

      <section data-bloco="proximos-recebimentos">
        <h2 className="mb-3 font-display text-lg font-bold text-titulo">Próximos recebimentos</h2>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {janelas.map((j) => (
            <div key={j.dias} className="surface p-4" data-janela={j.dias}>
              <Rotulo>Até {j.dias} dias</Rotulo>
              <p className={`mt-1.5 font-display text-xl font-bold tabular-nums ${j.valor > 0 ? "text-aviso-tinta" : "text-titulo"}`} data-valor>{formatBRL(j.valor)}</p>
              <p className="text-xs text-cinza">{plural(j.quantos, "lançamento", "lançamentos")}</p>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-cinza">O que falta receber, pelo vencimento, de hoje até cada janela. O que já venceu está nos Alertas.</p>
      </section>

      <Secao nome="liberados" titulo="Processos com valores liberados" descricao="Na etapa 9: o alvará expedido ou o valor liberado, esperando o levantamento.">
        {liberados.length === 0 ? (
          <p className="text-sm text-cinza">Nada esperando levantamento. Quando uma perícia chega à etapa de liberação, o valor a levantar aparece aqui.</p>
        ) : (
          <ul className="flex flex-col gap-2.5">
            {liberados.map((l) => (
              <li key={l.pericia.id} className="grid grid-cols-1 gap-3 rounded-cf border border-borda bg-fundo p-3.5 md:grid-cols-[minmax(0,1fr)_15rem_9rem] md:items-center" data-liberado={l.pericia.id}>
                <div className="min-w-0">
                  <Link to={`${RAIZ}/pericias/${l.pericia.id}`} className="font-semibold text-titulo hover:text-link hover:underline">{nomeDaPericia(l.pericia)}</Link>
                  <p className="truncate text-xs text-cinza tabular-nums">{[l.pericia.numeroProcesso, l.pericia.tribunal].filter(Boolean).join(" · ")}</p>
                </div>
                <EtapaProgresso pericia={l.pericia} />
                <div className="md:text-right"><Rotulo>A levantar</Rotulo><p className="font-display text-lg font-bold text-titulo tabular-nums" data-a-levantar>{formatBRL(l.aberto)}</p></div>
              </li>
            ))}
          </ul>
        )}
      </Secao>
    </>
  );
}

function CarneLeao({ query, mudar }: { query: URLSearchParams; mudar: (m: Record<string, string | null>) => void }) {
  const { pericias, podeEscrever } = usePerito();
  const anos = useMemo(() => anosDisponiveis(pericias), [pericias]);
  const anoDaQuery = Number(query.get("ano"));
  const ano = anos.includes(anoDaQuery) ? anoDaQuery : anos[0];
  const agrupar: VisaoDoCarne = (AGRUPAR.find((a) => a.id === query.get("agrupar"))?.id ?? "mensal") as VisaoDoCarne;
  const [abertos, setAbertos] = useState<Record<string, boolean>>({});
  const [editando, setEditando] = useState<Lancamento | null>(null);

  const lancamentos = useMemo(() => listarLancamentos(pericias, ano), [pericias, ano]);
  const resumo = useMemo(() => resumir(lancamentos), [lancamentos]);
  const linhas = useMemo(() => agruparCarne(lancamentos, agrupar, ano), [lancamentos, agrupar, ano]);
  const periciaEditando = editando ? pericias.find((p) => p.id === editando.periciaId) : undefined;
  const honorarioEditando = periciaEditando?.honorarios.find((h) => h.id === editando?.honorarioId) ?? null;

  const baixar = () => {
    const corpo: unknown[][] = [];
    for (const l of linhas) {
      corpo.push([l.rotulo, `${plural(l.lancamentos.length, "lançamento", "lançamentos")}`, "", numeroParaCsv(l.bruto), numeroParaCsv(l.inss), numeroParaCsv(l.irrf), numeroParaCsv(l.liquido)]);
      for (const x of l.lancamentos) corpo.push([formatarData(x.data), x.fontePagadora || x.numeroProcesso, x.cpfCnpj ? mascaraCpfCnpj(x.cpfCnpj) : "", numeroParaCsv(x.bruto), numeroParaCsv(x.inss), numeroParaCsv(x.irrf), numeroParaCsv(x.liquido)]);
    }
    corpo.push([`Total ${ano}`, "", "", numeroParaCsv(resumo.bruto), numeroParaCsv(resumo.inss), numeroParaCsv(resumo.irrf), numeroParaCsv(resumo.liquido)]);
    baixarCsv(montarCsv(["Período ou data", "Fonte pagadora", "CPF ou CNPJ", "Bruto", "INSS", "IRRF", "Líquido"], corpo), `carne-leao-${ano}-${agrupar}.csv`);
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm font-semibold text-tinta">
            Ano
            <select className={`${CAMPO} w-28`} value={ano} onChange={(e) => mudar({ ano: e.target.value })} data-ano>
              {anos.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <Alternador rotulo="Agrupar por" opcoes={AGRUPAR} valor={agrupar} aoMudar={(a) => mudar({ agrupar: a === "mensal" ? null : a })} />
        </div>
        {lancamentos.length > 0 && <button type="button" className={BOTAO_SECUNDARIO} onClick={baixar} data-csv-carne>Baixar a planilha</button>}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-resumo-do-carne>
        {([
          ["bruto", "Bruto", `recebido em ${ano}`],
          ["inss", "INSS", "retido ou recolhido"],
          ["irrf", "IRRF", "retido na fonte"],
          ["liquido", "Líquido", "bruto − INSS − IRRF"],
        ] as const).map(([chave, rotulo, nota]) => (
          <div key={chave} className="surface p-4" data-resumo={chave}>
            <Rotulo>{rotulo}</Rotulo>
            <p className={`mt-1.5 font-display text-xl font-bold tabular-nums ${chave === "liquido" ? "text-aviso-tinta" : "text-titulo"}`} data-valor>{formatBRL(resumo[chave])}</p>
            <p className="mt-0.5 text-xs text-cinza">{nota}</p>
          </div>
        ))}
      </div>

      <div className="surface overflow-hidden" data-tabela-do-carne>
        {lancamentos.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-cinza">Nenhum honorário recebido em {ano}. Lance o valor recebido e a data do recebimento nos honorários da perícia.</p>
        ) : (
          // `relative` no contêiner que rola: sem ele, o `sr-only` do cabeçalho (posição absoluta) tem a PÁGINA como
          // bloco de contenção, escapa do recorte e faz a página inteira rolar de lado no celular (769px em 390).
          <div className="relative overflow-x-auto">
            <table className="w-full min-w-[48rem] text-sm">
              <thead className="border-b border-borda bg-fundo">
                <tr className="text-left text-[11px] font-bold tracking-[0.12em] text-cinza uppercase [&>th]:px-4 [&>th]:py-3">
                  <th>Data</th><th>Fonte pagadora</th><th>CPF ou CNPJ</th><th className="text-right">Bruto</th><th className="text-right">INSS</th><th className="text-right">IRRF</th><th className="text-right">Líquido</th><th><span className="sr-only">Editar</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-borda">
                {linhas.map((linha) => {
                  const aberto = abertos[linha.chave] ?? agrupar === "anual";
                  return (
                    <Fragment key={linha.chave}>
                      <tr className="bg-fundo [&>td]:px-4 [&>td]:py-2.5" data-periodo={linha.chave}>
                        <td colSpan={2}>
                          <button type="button" aria-expanded={aberto} onClick={() => setAbertos((s) => ({ ...s, [linha.chave]: !aberto }))} className="inline-flex items-center gap-1.5 font-semibold text-titulo hover:text-link" data-abrir-periodo>
                            <span aria-hidden="true">{aberto ? "▾" : "▸"}</span> {linha.rotulo}
                          </button>
                        </td>
                        <td className="text-xs text-cinza">{plural(linha.lancamentos.length, "lançamento", "lançamentos")}</td>
                        <td className="text-right font-semibold tabular-nums" data-bruto>{formatBRL(linha.bruto)}</td>
                        <td className="text-right tabular-nums">{formatBRL(linha.inss)}</td>
                        <td className="text-right tabular-nums">{formatBRL(linha.irrf)}</td>
                        <td className="text-right font-semibold text-aviso-tinta tabular-nums">{formatBRL(linha.liquido)}</td>
                        <td />
                      </tr>
                      {aberto && linha.lancamentos.map((l) => (
                        <tr key={l.honorarioId} className="[&>td]:px-4 [&>td]:py-2.5" data-lancamento={l.honorarioId}>
                          <td className="whitespace-nowrap tabular-nums">{formatarData(l.data)}</td>
                          <td>{l.fontePagadora || <span className="text-cinza italic">informe a fonte na ficha da perícia</span>}<span className="block text-xs text-cinza tabular-nums">{l.numeroProcesso}</span></td>
                          <td className="text-xs tabular-nums">{l.cpfCnpj ? mascaraCpfCnpj(l.cpfCnpj) : "—"}</td>
                          <td className="text-right tabular-nums">{formatBRL(l.bruto)}</td>
                          <td className="text-right tabular-nums">{formatBRL(l.inss)}</td>
                          <td className="text-right tabular-nums">{formatBRL(l.irrf)}</td>
                          <td className="text-right text-aviso-tinta tabular-nums">{formatBRL(l.liquido)}</td>
                          <td className="text-right">
                            {podeEscrever && <button type="button" onClick={() => setEditando(l)} className="text-xs font-semibold text-link hover:underline" aria-label={`Editar o lançamento de ${formatarData(l.data)}`} data-editar-lancamento>Editar</button>}
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  );
                })}
                <tr className="border-t-2 border-t-laranja-texto font-semibold text-titulo [&>td]:px-4 [&>td]:py-3" data-total-do-ano>
                  <td colSpan={3}>Total {ano}</td>
                  <td className="text-right tabular-nums">{formatBRL(resumo.bruto)}</td>
                  <td className="text-right tabular-nums">{formatBRL(resumo.inss)}</td>
                  <td className="text-right tabular-nums">{formatBRL(resumo.irrf)}</td>
                  <td className="text-right text-aviso-tinta tabular-nums">{formatBRL(resumo.liquido)}</td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs leading-relaxed text-cinza" data-nota-do-irrf>
        O IRRF é o valor retido que está no comprovante do pagamento, informado no honorário. A Central não sugere o
        imposto: a tabela mensal de 2026 tem o redutor da Lei 15.270, e as fontes divergem sobre ele valer no cálculo
        de cada mês.
      </p>

      {periciaEditando && (
        <HonorarioDialogo pericia={periciaEditando} honorario={honorarioEditando} aberto={!!honorarioEditando} aoFechar={() => setEditando(null)} />
      )}
    </>
  );
}
